import { useIntl } from 'react-intl';
import { Badge } from '@windmill/react-ui';
import SigningForm from 'src/models/SigningForm';
import {
  getSigningFormPhase,
  signedByStudent,
  signedBySubjectResponsible,
  signedBySupervisor,
  signedByTeacher,
  signingFormCompleted,
  signingFormPendingSignature,
} from 'src/components/SigningForm/utils';

interface SigningFormStatusProps {
  signingForm: SigningForm | null;
}

interface SignatureSlotProps {
  signed: boolean;
  shortLabel: string;
  colorClassName: string;
  label: string;
}

function SignatureSlot({ signed, shortLabel, colorClassName, label }: SignatureSlotProps) {
  if (!signed) return null;
  return (
    <Badge type="neutral" role="img" aria-label={label} className={`${colorClassName} text-xs font-bold`}>
      {shortLabel}
    </Badge>
  );
}

export default function SigningFormStatus({ signingForm }: SigningFormStatusProps) {
  const { formatMessage } = useIntl();
  const phase = getSigningFormPhase(signingForm);
  const phaseLabel = formatMessage({ id: phase === 'midterm' ? 'signingFormPhaseMidterm' : 'signingFormPhaseEndterm' });

  const teacherSigned = signingForm ? signedByTeacher(signingForm) : false;
  const studentSigned = signingForm ? signedByStudent(signingForm) : false;
  const supervisorSigned = signingForm ? signedBySupervisor(signingForm) : false;
  const subjectResponsibleSigned = signingForm ? signedBySubjectResponsible(signingForm) : false;

  const completed = signingForm ? signingFormCompleted(signingForm) : false;
  const pendingSignature = signingForm ? signingFormPendingSignature(signingForm) : false;

  const signedLabel = formatMessage({ id: 'signingFormStatusSigned' });

  const showStatus = completed || pendingSignature;

  return (
    <span className="inline-flex items-center gap-3 ml-2">
      {!showStatus && (
        <span
          className={`text-sm font-bold px-2 py-1 rounded ${phase === 'endterm' ? 'bg-teacher text-teacherText' : 'bg-student text-studentText'}`}
        >
          {phaseLabel}
        </span>
      )}
      {showStatus && (
        <span className={`text-sm font-bold px-2 py-1 rounded ${completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
          {formatMessage({ id: completed ? 'signingFormStatusCompleted' : 'signingFormStatusPendingSignature' })}
        </span>
      )}
      <span className="inline-flex items-center gap-2">
        <SignatureSlot
          signed={teacherSigned}
          shortLabel="PL"
          colorClassName="bg-teacher text-teacherText"
          label={`${formatMessage({ id: 'signingFormStatusTeacher' })}: ${signedLabel}`}
        />
        <SignatureSlot
          signed={supervisorSigned}
          shortLabel="PV"
          colorClassName="bg-supervisor text-supervisorText"
          label={`${formatMessage({ id: 'signingFormStatusSupervisor' })}: ${signedLabel}`}
        />
        <SignatureSlot
          signed={studentSigned}
          shortLabel="S"
          colorClassName="bg-student text-studentText"
          label={`${formatMessage({ id: 'signingFormStatusStudent' })}: ${signedLabel}`}
        />
        <SignatureSlot
          signed={subjectResponsibleSigned}
          shortLabel="EA"
          colorClassName="bg-teacher text-teacherText"
          label={`${formatMessage({ id: 'subject-responsible' })}: ${signedLabel}`}
        />
      </span>
    </span>
  );
}
